import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icons,
  iconSize,
  InputAdornment,
  List,
  ListItemButton,
  palette,
  Stack,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useDirectory, useStartDirect, useStartGroup } from '../../lib/chat-api';

export interface NewConversationDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called with the conversation id once it exists on the server. */
  onOpened: (conversationId: string) => void;
}

/**
 * Starting a conversation: pick one colleague for a direct thread, or several
 * for a named group.
 *
 * **Why one dialog rather than two.** The difference between a DM and a group is
 * how many people you tick, and asking someone to decide that *before* seeing
 * the roster gets it wrong half the time. The group name only appears once a
 * second person is selected, because that is the first moment it is needed.
 *
 * The roster comes from `/chat/directory`, which is scoped to the officer's own
 * local governments — an empty list means no active appointment, and says so
 * rather than showing an unexplained blank.
 */
export function NewConversationDialog({ open, onClose, onOpened }: NewConversationDialogProps) {
  const { t } = useTranslation();
  // Only fetched while the dialog is open; the roster is irrelevant until then.
  const directory = useDirectory(open);
  const startDirect = useStartDirect();
  const startGroup = useStartGroup();

  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  const entries = directory.data ?? [];
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return entries;
    return entries.filter(
      (entry) =>
        entry.displayName.toLowerCase().includes(needle) ||
        entry.email.toLowerCase().includes(needle) ||
        // Searchable by what is shown: typing "clerk" should find the clerks.
        entry.roles.some((role) => t(`gov.admin.roles.${role}`).toLowerCase().includes(needle)),
    );
  }, [entries, query]);

  const isGroup = selected.length > 1;
  const pending = startDirect.isPending || startGroup.isPending;

  function reset() {
    setQuery('');
    setSelected([]);
    setTitle('');
    setError(null);
  }

  function toggle(userId: string) {
    setSelected((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  }

  async function submit() {
    setError(null);
    try {
      const conversation = isGroup
        ? await startGroup.mutateAsync({ title: title.trim(), memberUserIds: selected })
        : await startDirect.mutateAsync(selected[0]);
      reset();
      onOpened(conversation.id);
    } catch {
      // Deliberately generic: the failures here are a lost connection or a
      // colleague deactivated since the roster loaded, and neither is something
      // the officer can act on differently.
      setError(t('common.chat.startFailed'));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{t('common.chat.newConversation')}</DialogTitle>
      <DialogContent dividers>
        <Stack sx={{ gap: 2 }}>
          <TextField
            fullWidth
            size="small"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('common.chat.searchColleagues')}
            aria-label={t('common.chat.searchColleagues')}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Icons.Search size={iconSize.md} aria-hidden />
                  </InputAdornment>
                ),
              },
            }}
          />

          {isGroup && (
            <TextField
              fullWidth
              size="small"
              required
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              label={t('common.chat.groupName')}
            />
          )}

          {error && <Alert severity="error">{error}</Alert>}

          <Box
            sx={{ maxHeight: 320, overflowY: 'auto', border: `1px solid ${palette.border.line}` }}
          >
            {directory.isLoading ? (
              <Typography variant="body2" sx={{ p: 2, color: palette.text.muted }}>
                {t('common.chat.loadingColleagues')}
              </Typography>
            ) : entries.length === 0 ? (
              <Typography variant="body2" sx={{ p: 2, color: palette.text.muted }}>
                {t('common.chat.noColleagues')}
              </Typography>
            ) : filtered.length === 0 ? (
              <Typography variant="body2" sx={{ p: 2, color: palette.text.muted }}>
                {t('common.chat.noColleagueMatches')}
              </Typography>
            ) : (
              <List disablePadding>
                {filtered.map((entry) => (
                  <ListItemButton
                    key={entry.userId}
                    onClick={() => toggle(entry.userId)}
                    sx={{ gap: 1.5 }}
                  >
                    <Checkbox
                      edge="start"
                      checked={selected.includes(entry.userId)}
                      tabIndex={-1}
                      disableRipple
                      // The row is the control; the checkbox is a state readout,
                      // so it must not be a second tab stop announcing itself.
                      slotProps={{
                        input: { 'aria-labelledby': `chat-colleague-${entry.userId}` },
                      }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography id={`chat-colleague-${entry.userId}`} variant="body2" noWrap>
                        {entry.displayName}
                      </Typography>
                      {/* What they are, then how to reach them. A name and an
                          address tell you nothing when you are looking for
                          "whoever handles resolutions at Moncton" — the role is
                          the thing being searched for, so it leads. */}
                      <Typography variant="caption" sx={{ color: palette.text.muted }} noWrap>
                        {entry.roles.length > 0
                          ? `${entry.roles.map((role) => t(`gov.admin.roles.${role}`)).join(' · ')} — ${entry.email}`
                          : entry.email}
                      </Typography>
                    </Box>
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            reset();
            onClose();
          }}
        >
          {t('common.actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={selected.length === 0 || (isGroup && !title.trim()) || pending}
        >
          {isGroup ? t('common.chat.createGroup') : t('common.chat.startChat')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
